<template>
  <div class="space-y-6">
    <!-- Toggle Switches -->
    <div class="flex items-center gap-6">
      <label class="flex items-center gap-2 cursor-pointer">
        <button
          @click="toggleGa"
          :disabled="gaToggling"
          :class="[
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
            localGaEnabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
          ]">
          <span
            :class="[
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              localGaEnabled ? 'translate-x-6' : 'translate-x-1'
            ]" />
        </button>
        <span class="text-sm text-gray-700 dark:text-gray-300">GA4 Tracking</span>
        <svg v-if="gaSyncing" class="w-4 h-4 animate-spin text-primary-500" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </label>

      <label class="flex items-center gap-2 cursor-pointer">
        <button
          @click="toggleGsc"
          :disabled="gscToggling"
          :class="[
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
            localGscEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
          ]">
          <span
            :class="[
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              localGscEnabled ? 'translate-x-6' : 'translate-x-1'
            ]" />
        </button>
        <span class="text-sm text-gray-700 dark:text-gray-300">GSC Tracking</span>
        <svg v-if="gscSyncing" class="w-4 h-4 animate-spin text-green-500" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </label>
    </div>

    <!-- Sync Status -->
    <div v-if="gaSyncing || gscSyncing" class="p-3 text-sm text-blue-800 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300 rounded-lg">
      Syncing historical data (up to 13 months). This may take a few minutes...
    </div>

    <!-- Date Range Selector -->
    <div v-if="localGaEnabled || localGscEnabled" class="flex items-center justify-end">
      <select
        v-model="dateRange"
        @change="fetchAllData"
        class="text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg">
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-8">
      <svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-600" viewBox="0 0 100 101" fill="none">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
      </svg>
    </div>

    <!-- Not Enabled State -->
    <div v-else-if="!localGaEnabled && !localGscEnabled" class="text-center py-8">
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Enable GA4 or GSC tracking above to start collecting page-level analytics.
      </p>
    </div>

    <template v-else>
      <!-- GA4 Section -->
      <div v-if="localGaEnabled && gaStats">
        <h4 class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
          Google Analytics (GA4)
        </h4>

        <!-- Stat Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Sessions</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ formatNumber(gaStats.totals.sessions) }}</p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Users</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ formatNumber(gaStats.totals.total_users) }}</p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Page Views</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ formatNumber(gaStats.totals.page_views) }}</p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Bounce Rate</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ formatPercent(gaStats.totals.bounce_rate) }}</p>
          </div>
        </div>

        <!-- Sessions by Day Chart -->
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-3">
          <h5 class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Sessions by Day</h5>
          <div class="flex items-end space-x-1 h-24">
            <div
              v-for="day in gaDailyChart"
              :key="day.date"
              :title="`${day.date}: ${day.sessions} sessions`"
              class="flex-1 bg-primary-500 dark:bg-primary-400 rounded-t min-h-[2px] transition-all hover:bg-primary-600"
              :style="{ height: day.heightPercent + '%' }" />
          </div>
          <div class="flex justify-between mt-1">
            <span class="text-[10px] text-gray-400">{{ gaDailyChart[0]?.date }}</span>
            <span class="text-[10px] text-gray-400">{{ gaDailyChart[gaDailyChart.length - 1]?.date }}</span>
          </div>
        </div>

        <!-- Device Breakdown -->
        <div v-if="gaStats.devices?.length" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-3">
          <h5 class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Sessions by Device</h5>
          <div class="space-y-2">
            <div v-for="device in deviceBreakdown" :key="device.name" class="flex items-center gap-3">
              <span class="text-xs text-gray-600 dark:text-gray-400 w-16 capitalize">{{ device.name }}</span>
              <div class="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  :class="device.colorClass"
                  class="h-full rounded-full transition-all"
                  :style="{ width: device.percent + '%' }" />
              </div>
              <span class="text-xs text-gray-600 dark:text-gray-400 w-20 text-right">
                {{ formatNumber(device.sessions) }} ({{ device.percent }}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- GSC Section -->
      <div v-if="localGscEnabled && gscStats">
        <h4 class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3 mt-6">
          Search Console (GSC)
        </h4>

        <!-- Stat Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Clicks</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ formatNumber(gscStats.totals.clicks) }}</p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Impressions</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ formatNumber(gscStats.totals.impressions) }}</p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Avg CTR</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ formatPercent(gscStats.totals.avg_ctr) }}</p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">Avg Position</p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{{ gscStats.totals.avg_position?.toFixed(1) || '—' }}</p>
          </div>
        </div>

        <!-- Clicks by Day Chart -->
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-3">
          <h5 class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Clicks by Day</h5>
          <div class="flex items-end space-x-1 h-24">
            <div
              v-for="day in gscDailyChart"
              :key="day.date"
              :title="`${day.date}: ${day.clicks} clicks`"
              class="flex-1 bg-green-500 dark:bg-green-400 rounded-t min-h-[2px] transition-all hover:bg-green-600"
              :style="{ height: day.heightPercent + '%' }" />
          </div>
          <div class="flex justify-between mt-1">
            <span class="text-[10px] text-gray-400">{{ gscDailyChart[0]?.date }}</span>
            <span class="text-[10px] text-gray-400">{{ gscDailyChart[gscDailyChart.length - 1]?.date }}</span>
          </div>
        </div>

        <!-- Query Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mt-3">
          <div class="p-4 border-b border-gray-200 dark:border-gray-700">
            <h5 class="text-xs font-medium text-gray-500 dark:text-gray-400">Top Search Queries</h5>
          </div>
          <div v-if="queriesLoading" class="flex justify-center py-6">
            <svg aria-hidden="true" class="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
            </svg>
          </div>
          <table v-else-if="queries.length" class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                <th class="px-4 py-2">Query</th>
                <th class="px-4 py-2 text-right">Clicks</th>
                <th class="px-4 py-2 text-right">Impr.</th>
                <th class="px-4 py-2 text-right">CTR</th>
                <th class="px-4 py-2 text-right">Pos.</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="q in queries"
                :key="q.query"
                class="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td class="px-4 py-2 text-gray-900 dark:text-gray-100 truncate max-w-[200px]" :title="q.query">{{ q.query }}</td>
                <td class="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{{ q.clicks }}</td>
                <td class="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{{ formatNumber(q.impressions) }}</td>
                <td class="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{{ formatPercent(q.ctr) }}</td>
                <td class="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{{ q.position?.toFixed(1) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No query data available yet.
          </div>
          <!-- Show More -->
          <div v-if="hasMoreQueries" class="p-3 border-t border-gray-200 dark:border-gray-700 text-center">
            <button @click="loadMoreQueries" :disabled="queriesLoading" class="text-sm text-primary-600 dark:text-primary-400 hover:underline">
              Show more
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
  enablePageGa, enablePageGsc, disablePageGa, disablePageGsc,
  getPageGaStats, getPageGscStats, getPageGscQueries
} from '~/javascript/api/website_pages.js'
import consumer from '~/channels/consumer'

const props = defineProps({
  pageId: { type: String, required: true },
  gaEnabled: { type: Boolean, default: false },
  gscEnabled: { type: Boolean, default: false }
})

const emit = defineEmits(['updated'])

const loading = ref(false)
const localGaEnabled = ref(props.gaEnabled)
const localGscEnabled = ref(props.gscEnabled)
const gaToggling = ref(false)
const gscToggling = ref(false)
const gaSyncing = ref(false)
const gscSyncing = ref(false)
const dateRange = ref('30')
const gaStats = ref(null)
const gscStats = ref(null)
const queries = ref([])
const queriesLoading = ref(false)
const queriesPage = ref(1)
const totalQueries = ref(0)
let subscription = null

const hasMoreQueries = computed(() => queries.value.length < totalQueries.value)

const gaDailyChart = computed(() => {
  if (!gaStats.value?.daily) return []
  const entries = gaStats.value.daily
  const max = Math.max(...entries.map(e => e.sessions), 1)
  return entries.map(e => ({ ...e, heightPercent: (e.sessions / max) * 100 }))
})

const gscDailyChart = computed(() => {
  if (!gscStats.value?.daily) return []
  const entries = gscStats.value.daily
  const max = Math.max(...entries.map(e => e.clicks), 1)
  return entries.map(e => ({ ...e, heightPercent: (e.clicks / max) * 100 }))
})

const deviceBreakdown = computed(() => {
  if (!gaStats.value?.devices) return []
  const total = gaStats.value.devices.reduce((sum, d) => sum + d.sessions, 0) || 1
  const colors = { desktop: 'bg-primary-500', mobile: 'bg-amber-500', tablet: 'bg-green-500', 'smart tv': 'bg-purple-500' }
  return gaStats.value.devices
    .sort((a, b) => b.sessions - a.sessions)
    .map(d => ({
      name: d.device,
      sessions: d.sessions,
      percent: Math.round((d.sessions / total) * 100),
      colorClass: colors[d.device] || 'bg-gray-400'
    }))
})

const formatNumber = (n) => {
  if (!n) return '0'
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return n.toString()
}

const formatPercent = (n) => `${((n || 0) * 100).toFixed(1)}%`

const dateParams = () => {
  const days = parseInt(dateRange.value, 10)
  return {
    start_date: new Date(Date.now() - days * 86400000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  }
}

const fetchGaData = async () => {
  if (!localGaEnabled.value) return
  try {
    const resp = await getPageGaStats(props.pageId, dateParams())
    gaStats.value = resp.data
  } catch (e) { console.error('GA stats error:', e) }
}

const fetchGscData = async () => {
  if (!localGscEnabled.value) return
  try {
    const resp = await getPageGscStats(props.pageId, dateParams())
    gscStats.value = resp.data
  } catch (e) { console.error('GSC stats error:', e) }
}

const fetchQueries = async (reset = true) => {
  if (!localGscEnabled.value) return
  queriesLoading.value = true
  if (reset) { queriesPage.value = 1; queries.value = [] }
  try {
    const resp = await getPageGscQueries(props.pageId, { ...dateParams(), page: queriesPage.value, per: 25 })
    totalQueries.value = parseInt(resp.headers['total-count'] || '0', 10)
    if (reset) {
      queries.value = resp.data
    } else {
      queries.value = [...queries.value, ...resp.data]
    }
  } catch (e) { console.error('Query stats error:', e) }
  finally { queriesLoading.value = false }
}

const loadMoreQueries = () => {
  queriesPage.value++
  fetchQueries(false)
}

const fetchAllData = async () => {
  loading.value = true
  await Promise.all([fetchGaData(), fetchGscData(), fetchQueries()])
  loading.value = false
}

const toggleGa = async () => {
  gaToggling.value = true
  try {
    if (localGaEnabled.value) {
      await disablePageGa(props.pageId)
      localGaEnabled.value = false
      gaStats.value = null
    } else {
      await enablePageGa(props.pageId)
      localGaEnabled.value = true
      gaSyncing.value = true
    }
    emit('updated')
  } catch (e) { console.error('Toggle GA error:', e) }
  finally { gaToggling.value = false }
}

const toggleGsc = async () => {
  gscToggling.value = true
  try {
    if (localGscEnabled.value) {
      await disablePageGsc(props.pageId)
      localGscEnabled.value = false
      gscStats.value = null
      queries.value = []
    } else {
      await enablePageGsc(props.pageId)
      localGscEnabled.value = true
      gscSyncing.value = true
    }
    emit('updated')
  } catch (e) { console.error('Toggle GSC error:', e) }
  finally { gscToggling.value = false }
}

onMounted(() => {
  if (localGaEnabled.value || localGscEnabled.value) {
    fetchAllData()
  }

  subscription = consumer.subscriptions.create(
    { channel: 'WebsitePageSyncChannel', page_id: props.pageId },
    {
      received(data) {
        if (data.event === 'ga_sync_complete') {
          gaSyncing.value = false
          fetchGaData()
        } else if (data.event === 'gsc_sync_complete') {
          gscSyncing.value = false
          fetchGscData()
          fetchQueries()
        } else if (data.event === 'ga_sync_failed') {
          gaSyncing.value = false
        } else if (data.event === 'gsc_sync_failed') {
          gscSyncing.value = false
        }
      }
    }
  )
})

onUnmounted(() => {
  if (subscription) subscription.unsubscribe()
})
</script>
